import Echo from '@ably/laravel-echo'
import {useCallback, useEffect, useState} from 'react'

type Channel = ReturnType<Echo['private']>

export function usePrivateChannel(params: {
  echo: Echo
  name: string | null
  onConnect?: () => void
  onDisconnect?: () => void
}) {
  const [socketId, setSocketId] = useState<string | null>(null)
  const [channel, setChannel] = useState<Channel | null>(null)
  const disconnect = useCallback(() => {
    setSocketId(null)
  }, [])

  const {echo, name, onConnect, onDisconnect} = params

  const handleConnected = useCallback(
    (connectionId: string) => {
      setSocketId(connectionId)
      onConnect && onConnect()
    },
    [onConnect, setSocketId],
  )

  useEffect(() => {
    if (!name) {
      return
    }

    const privateChannel = echo.private(name).error((error: {code: number}) => {
      /**
       * Handle authentication failure which would mean we would never receive auth updates
       * Ably error code 40142 = token expired
       * Ably error code 40100 = unauthorized
       */
      if (error.code === 40100 || error.code === 40142) {
        // disconnect()
      }
    })

    const connection = echo.connector.ably.connection

    // If the socket is already connected, we'll set it immediately,
    // otherwise we'll wait for the `connected` event to fire.
    if (connection.state === 'connected' && connection.id) {
      handleConnected(connection.id)
    } else {
      connection.on('connected', () => {
        handleConnected(connection.id || '')
      })
    }

    connection.on('disconnected', disconnect)

    setChannel(privateChannel)

    return () => {
      if (!name) {
        return
      }

      echo.leave(name)
      onDisconnect && onDisconnect()
    }
  }, [echo, disconnect, name, onDisconnect, handleConnected])

  const connected = Boolean(socketId) && Boolean(channel)
  return {socketId, channel, connected}
}
